import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, formatApiError } from "@/lib/api";
import { dubaiToday, fmtDateLong, fmtDateShort, fmtTime, TZ_LABEL } from "@/lib/format";
import { StatusBadge } from "./StatusBadge";

function Row({ k, v, tid }) {
  return (
    <div className="py-3 border-b border-[rgba(20,35,55,0.08)] grid grid-cols-3 gap-3">
      <dt className="text-xs text-[#697386]">{k}</dt>
      <dd className="col-span-2 text-sm text-[#172033] break-words" data-testid={tid}>{v || "—"}</dd>
    </div>
  );
}

function Reschedule({ row, onDone }) {
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [pick, setPick] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!date) {
      return;
    }
    setSlots(null);
    setPick(null);
    api.get("/admin/slots", { params: { date, exclude_id: row.id } }).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [date, row.id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/appointments/${row.id}/reschedule`, { start_utc: pick });
      toast.success("Appointment rescheduled");
      onDone(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  return (
    <div className="mt-4 rounded-2xl border border-[rgba(20,35,55,0.10)] p-4 bg-[#F5F7FA]" data-testid="reschedule-panel">
      <label className="text-xs font-medium text-[#697386]" htmlFor="resched-date">New date</label>
      <input id="resched-date" type="date" min={dubaiToday()} value={date} onChange={(e) => setDate(e.target.value)} className="field mt-1" data-testid="reschedule-date-input" />
      {date && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {slots === null && <Loader2 size={16} className="animate-spin text-[#697386]" />}
          {slots?.filter((s) => s.available).map((s) => (
            <button key={s.start_utc} onClick={() => setPick(s.start_utc)} className={`rounded-xl py-2 text-xs font-semibold border ${pick === s.start_utc ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "bg-white border-[rgba(20,35,55,0.10)]"}`} data-testid={`reschedule-slot-${s.time}`}>
              {s.label}
            </button>
          ))}
          {slots && !slots.some((s) => s.available) && <p className="col-span-3 text-xs text-[#697386]">No open times on this date.</p>}
        </div>
      )}
      <button disabled={!pick || saving} onClick={save} className="btn-primary w-full mt-4 !py-2.5 !text-sm" data-testid="reschedule-confirm-button">
        {saving ? <Loader2 size={14} className="animate-spin" /> : "Confirm new time"}
      </button>
    </div>
  );
}

export function RegistrationSheet({ id, onClose, onChanged }) {
  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showResched, setShowResched] = useState(false);

  useEffect(() => {
    setRow(null);
    setShowResched(false);
    if (!id) {
      return;
    }
    api.get(`/admin/registrations/${id}`).then((r) => setRow(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  const updated = (data) => {
    setRow((r) => ({ ...r, ...data }));
    setShowResched(false);
    onChanged?.();
  };

  const setStatus = async (status) => {
    if (status === "cancelled" && !window.confirm("Cancel this appointment? The visitor will be notified by email.")) return;
    setBusy(status);
    try {
      const { data } = await api.patch(`/admin/appointments/${id}/status`, { status });
      toast.success(`Marked as ${status}`);
      updated(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(null);
  };

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="registration-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-[#0B1F3A]">{row?.full_name || "Loading…"}</SheetTitle>
          <SheetDescription>Registration details</SheetDescription>
        </SheetHeader>
        {row && (
          <div className="mt-6">
            <div className="rounded-2xl bg-[#0B1F3A] text-white p-5">
              <div className="flex items-center justify-between"><p className="text-xs text-white/50">Appointment</p><StatusBadge status={row.status} /></div>
              <p className="mt-2 font-semibold" data-testid="detail-appt-date">{fmtDateLong(row.start_utc)}</p>
              <p className="text-white/80 text-sm" data-testid="detail-appt-time">{fmtTime(row.start_utc)} – {fmtTime(row.end_utc)} · {TZ_LABEL}</p>
            </div>
            <dl className="mt-4">
              <Row k="Email" v={row.email} tid="detail-email" />
              <Row k="WhatsApp" v={row.whatsapp} tid="detail-whatsapp" />
              <Row k="Country" v={row.country} tid="detail-country" />
              <Row k="Visitor time zone" v={row.visitor_timezone ? `${row.visitor_timezone.replace(/_/g, " ")} — ${fmtTime(row.start_utc, row.visitor_timezone)} their time` : null} tid="detail-visitor-tz" />
              <Row k="Budget (USD)" v={row.budget} tid="detail-budget" />
              <Row k="Question" v={row.question} tid="detail-question" />
              <Row k="Registered" v={`${fmtDateShort(row.created_at)}, ${fmtTime(row.created_at)}`} tid="detail-created" />
            </dl>
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-[#697386]">Update status</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {["confirmed", "completed", "cancelled"].map((s) => (
                <button key={s} disabled={busy || row.status === s} onClick={() => setStatus(s)} className="rounded-xl border border-[rgba(20,35,55,0.12)] bg-white py-2.5 text-sm font-medium capitalize hover:bg-[#F5F7FA] disabled:opacity-40" data-testid={`detail-status-${s}-button`}>
                  {busy === s ? <Loader2 size={14} className="animate-spin mx-auto" /> : `Mark ${s}`}
                </button>
              ))}
              <button onClick={() => setShowResched((v) => !v)} className="rounded-xl border border-[#C7A76C]/50 bg-[#C7A76C]/10 py-2.5 text-sm font-medium text-[#7A5F2E]" data-testid="detail-reschedule-toggle">Reschedule</button>
            </div>
            {showResched && <Reschedule row={row} onDone={updated} />}
            {row.history?.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#697386]">History</p>
                <ul className="mt-2 space-y-1 text-xs text-[#697386]" data-testid="detail-history">
                  {row.history.map((h, i) => <li key={i}>{fmtDateShort(h.at)}, {fmtTime(h.at)} — <span className="capitalize">{h.action}</span>{h.start_utc ? ` for ${fmtDateShort(h.start_utc)} ${fmtTime(h.start_utc)}` : ""}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
